import { useMutation as useReactQueryMutation, type DefaultError, type QueryClient, type UseMutationOptions, type UseMutationResult } from "@tanstack/react-query";

/**
 * Maintenance writes are deliberate foreground operations, never queued work.
 * `always` prevents React Query's offline pause/reconnect replay. The explicit
 * guard fails BEFORE mutation functions run; apiRequest also
 * guards connectivity immediately before the real credentialed request.
 */
export function useMutation<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>, client?: QueryClient,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const mutationFn: NonNullable<typeof options.mutationFn> = async (...args) => {
    if (typeof navigator !== "undefined" && navigator.onLine === false)
      throw new Error("Offline. Nothing was submitted. Reconnect and tap Save again.");
    if (!options.mutationFn) throw new Error("Maintenance operation unavailable.");
    return options.mutationFn(...args);
  };
  return useReactQueryMutation({ ...options, mutationFn, networkMode: "always", retry: false }, client);
}
